import React, { useEffect, useRef, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, InputAdornment, Tab, Tabs, TextField, Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import RouterOutlinedIcon from "@mui/icons-material/RouterOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SyncProblemOutlinedIcon from "@mui/icons-material/SyncProblemOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import BlockIcon from "@mui/icons-material/Block";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";

import { ACCESS_EVENT_LABELS } from "../attendance/zkStatusStyles";
import { IconBubble, InfoTip, Panel, PanelState, StatusBadge, gridSx, sectionState, useSurface } from "./OverviewUi";
import { MethodCategoryFilters } from "./OverviewCharts";
import { typeLabel, valueLabel } from "../promotions/promotionApi";
import {
  METHOD_META, PALETTE, formatLkr, longDate, overviewApi, plural, rangeText, requestError,
  shortDate, timeOf, titleCase,
} from "./overviewUtils";

// ---------------------------------------------------------------- columns (shared with the drawers)
export const checkInColumns = (today) => [
  { field: "full_name", headerName: "Member", flex: 1.2, minWidth: 150, cellClassName: "name-column--cell" },
  { field: "membership_id", headerName: "Membership ID", flex: 1, minWidth: 140 },
  {
    field: "check_in", headerName: "Check-in", flex: 0.9, minWidth: 120, sortable: false,
    valueGetter: (v, row) => (row.check_in
      ? `${row.attendance_date === today ? "Today" : shortDate(row.attendance_date)} ${timeOf(row.check_in)}` : "-"),
  },
  {
    field: "status_on_day", headerName: "Status", flex: 1.1, minWidth: 170, sortable: false,
    renderCell: ({ row }) => <StatusBadge status={row.status_on_day} blocked={row.blocked_on_day} />,
  },
];

export const paymentColumns = [
  { field: "receipt_number", headerName: "Receipt", flex: 1, minWidth: 160 },
  { field: "full_name", headerName: "Member", flex: 1.2, minWidth: 150, cellClassName: "name-column--cell" },
  { field: "membership_id", headerName: "Membership ID", flex: 1, minWidth: 140 },
  { field: "membership_type", headerName: "Package", flex: 1, minWidth: 130, valueGetter: (v) => titleCase(v) },
  {
    field: "payment_method", headerName: "Method", flex: 0.8, minWidth: 120,
    valueGetter: (v) => (METHOD_META[v] || { label: titleCase(v) }).label,
  },
  { field: "payment_date", headerName: "Date", flex: 0.8, minWidth: 110, valueGetter: (v) => longDate(v) },
  {
    field: "amount", headerName: "Amount", flex: 0.9, minWidth: 120, align: "right", headerAlign: "right",
    valueGetter: (v) => formatLkr(v, 2),
  },
];

/** First page (10 rows) of a paged overview list; reloads when ``params`` or ``refreshKey`` change. */
const useFirstPage = (path, params, refreshKey, enabled = true) => {
  const [result, setResult] = useState({ data: null, error: "" });
  const latest = useRef(0);
  const key = JSON.stringify(params);
  useEffect(() => {
    if (!enabled) return;
    const id = ++latest.current;
    overviewApi(path, { ...JSON.parse(key), page: 1, page_size: 10 })
      .then((data) => { if (id === latest.current) setResult({ data, error: "" }); })
      .catch((e) => { if (id === latest.current) setResult((r) => ({ data: r.data, error: requestError(e, "Could not load this list.") })); });
  }, [path, key, refreshKey, enabled]);
  return result;
};

function SmallGrid({ rows, columns, onRowClick, getRowId = (r) => r.id }) {
  const s = useSurface();
  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      <DataGrid rows={rows} columns={columns} getRowId={getRowId} autoHeight hideFooter disableRowSelectionOnClick
        disableColumnMenu density="standard" rowHeight={46} columnHeaderHeight={44}
        onRowClick={onRowClick} sx={gridSx(s)} />
    </Box>
  );
}

const viewAllButton = (onClick, disabled) => (
  <Button size="small" variant="outlined" onClick={onClick} disabled={disabled} sx={{ textTransform: "none", fontSize: 13 }}>
    View all
  </Button>
);

// ---------------------------------------------------------------- recent check-ins
export function RecentCheckIns({ range, today, refreshKey, onViewAll, onMember }) {
  const { data, error } = useFirstPage("/check-ins", { from: range.from, to: range.to }, refreshKey);
  const state = data ? sectionState(data, false) : error ? "error" : "loading";
  return (
    <Panel
      icon={<IconBubble Icon={EventAvailableOutlinedIcon} color={PALETTE.blue} size={36} />}
      title="Recent check-ins"
      subtitle="Latest 10"
      info="Status is shown as it was on the day."
      action={viewAllButton(onViewAll, !data?.total_count)}
    >
      {error && data && <Alert severity="warning" variant="outlined" sx={{ mb: 1 }}>{error}</Alert>}
      <PanelState state={state} reason={data?.reason} empty={!data?.items?.length} height={320}
        emptyText={`No check-ins between ${rangeText(range.from, range.to)}.`}>
        <SmallGrid rows={data?.items || []} columns={checkInColumns(today)} onRowClick={({ row }) => onMember(row.membership_id)} />
        <Typography sx={{ fontSize: 12.5, mt: 1, color: "text.secondary" }}>
          {plural(data?.total_count || 0, "check-in")}
        </Typography>
      </PanelState>
    </Panel>
  );
}

// ---------------------------------------------------------------- recent payments
export function RecentPayments({ range, refreshKey, onViewAll, onMember }) {
  const s = useSurface();
  const [method, setMethod] = useState("all");
  const [category, setCategory] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const filters = { from: range.from, to: range.to, method, category, q: search };
  const { data, error } = useFirstPage("/payments", filters, refreshKey);
  const state = data ? sectionState(data, false) : error ? "error" : "loading";
  const filteredText = method !== "all" || category !== "all" || search ? " for these filters" : "";
  return (
    <Panel
      icon={<IconBubble Icon={ReceiptLongOutlinedIcon} color={PALETTE.purple} size={36} />}
      title="Recent payments"
      subtitle="Latest 10"
      action={viewAllButton(() => onViewAll({ method, category, search }), !data?.total_count)}
    >
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
        <MethodCategoryFilters method={method} category={category} onMethod={setMethod} onCategory={setCategory} />
        <TextField size="small" value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Name, membership ID or receipt" sx={{ flex: "1 1 220px", "& input": { fontSize: 13 } }}
          inputProps={{ "aria-label": "Search payments" }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
      </Box>
      {error && data && <Alert severity="warning" variant="outlined" sx={{ mb: 1 }}>{error}</Alert>}
      <PanelState state={state} reason={data?.reason} empty={!data?.items?.length} height={320}
        emptyText={`No payments between ${rangeText(range.from, range.to)}${filteredText}.`}>
        <SmallGrid rows={data?.items || []} columns={paymentColumns} onRowClick={({ row }) => onMember(row.membership_id)} />
        <Typography sx={{ fontSize: 13, mt: 1, color: s.muted }}>
          Total: <Box component="span" sx={{ fontWeight: 800, color: s.text }}>{formatLkr(data?.total_amount, 2)}</Box>
          {" "}· {plural(data?.total_count || 0, "payment")}
        </Typography>
      </PanelState>
    </Panel>
  );
}

// ---------------------------------------------------------------- expiring soon
const daysColor = (days) => (days <= 3 ? PALETTE.red : days <= 7 ? PALETTE.amber : PALETTE.green);

function DaysLeft({ days, window }) {
  const color = daysColor(days);
  const Icon = days <= 3 ? ErrorOutlineIcon : days <= 7 ? WarningAmberOutlinedIcon : CheckCircleOutlineIcon;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box sx={{ position: "relative", display: "inline-flex" }}>
        <CircularProgress variant="determinate" value={100} size={28} thickness={5} sx={{ color: "rgba(127,127,127,0.2)", position: "absolute" }} />
        <CircularProgress variant="determinate" value={Math.max(4, Math.min(100, (days / window) * 100))} size={28} thickness={5} sx={{ color }} />
      </Box>
      <Icon sx={{ fontSize: 16, color }} aria-hidden />
      <Typography sx={{ fontSize: 13, fontWeight: 700, color }}>{days === 0 ? "Today" : plural(days, "day")}</Typography>
    </Box>
  );
}

export function ExpiringSoon({ section, failed, windowDays, onWindow, onMember }) {
  const s = useSurface();
  const state = sectionState(section, failed);
  const current = section?.windows?.[windowDays];
  const columns = [
    { field: "full_name", headerName: "Member", flex: 1.2, minWidth: 150, cellClassName: "name-column--cell" },
    { field: "membership_id", headerName: "Membership ID", flex: 1, minWidth: 140 },
    { field: "membership_type", headerName: "Package", flex: 1, minWidth: 130, valueGetter: (v) => titleCase(v) },
    { field: "expiry_date", headerName: "Expiry date", flex: 0.9, minWidth: 120, valueGetter: (v) => longDate(v) },
    {
      field: "days_left", headerName: "Days left", flex: 1, minWidth: 150,
      renderCell: ({ row }) => <DaysLeft days={row.days_left} window={Number(windowDays)} />,
    },
  ];
  return (
    <Panel
      id="hpu-expiring-soon"
      icon={<IconBubble Icon={EventBusyOutlinedIcon} color={PALETTE.amber} size={36} />}
      title="Expiring soon"
      subtitle="Active members"
      info="Frozen days are added to the expiry date."
    >
      <Tabs value={windowDays} onChange={(e, v) => onWindow(v)} textColor="inherit" variant="scrollable"
        allowScrollButtonsMobile sx={{ mb: 1.5, "& .MuiTabs-indicator": { backgroundColor: PALETTE.amber } }}>
        {["7", "14", "30"].map((w) => (
          <Tab key={w} value={w} sx={{ textTransform: "none", fontSize: 14, fontWeight: 700, "&.Mui-selected": { color: PALETTE.amber } }}
            label={`${w} days${section?.windows?.[w] ? ` (${section.windows[w].count})` : ""}`} />
        ))}
      </Tabs>
      <PanelState state={state} reason={section?.reason} empty={!current?.items?.length} height={300}
        emptyText={`No active member expires within ${windowDays} days.`}>
        <SmallGrid rows={current?.items || []} columns={columns} getRowId={(r) => r.registration_id}
          onRowClick={({ row }) => onMember(row.membership_id)} />
        {current && current.count > current.items.length && (
          <Typography sx={{ fontSize: 12.5, color: s.muted, mt: 1 }}>
            First {current.items.length} of {current.count}
          </Typography>
        )}
      </PanelState>
    </Panel>
  );
}

// ---------------------------------------------------------------- alerts
function AlertRow({ Icon, color, label, value, detail, onClick }) {
  const s = useSurface();
  return (
    <Box role="button" tabIndex={0} onClick={onClick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onClick(); }}
      sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1.25, mb: 1, borderRadius: 2, cursor: "pointer",
        border: `1px solid ${s.border}`, borderLeft: `4px solid ${color}`,
        "&:hover, &:focus": { bgcolor: s.hover, outline: "none" } }}>
      <Icon sx={{ color }} aria-hidden />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: s.text }}>{label}</Typography>
        {detail && <Typography sx={{ fontSize: 13, color: s.muted }}>{detail}</Typography>}
      </Box>
      <Typography sx={{ fontSize: 22, fontWeight: 800, color }}>{value ?? "—"}</Typography>
      <ChevronRightIcon sx={{ color: s.muted }} aria-hidden />
    </Box>
  );
}

export const MODE_META = {
  on: { label: "On", color: PALETTE.green, Icon: CheckCircleOutlineIcon },
  dryrun: { label: "Dry-run", color: PALETTE.blue, Icon: InfoOutlinedIcon },
  paused: { label: "Paused", color: PALETTE.amber, Icon: WarningAmberOutlinedIcon },
  off: { label: "Off", color: PALETTE.grey, Icon: BlockIcon },
};

const waiting = (days) => (days == null ? null : `Oldest: ${plural(days, "day")}`);

export function AlertsPanel({ alerts, failed, zkOn, go }) {
  const expired = alerts?.expired;
  const pending = alerts?.pending;
  const door = alerts?.door;
  const mode = MODE_META[door?.mode] || MODE_META.off;
  return (
    <Panel icon={<IconBubble Icon={NotificationsActiveOutlinedIcon} color={PALETTE.red} size={36} />}
      title="Alerts" subtitle="Today">
      {sectionState(expired, failed) === "ready" ? (
        <AlertRow Icon={ErrorOutlineIcon} color={PALETTE.red} label="Expired (incl. grace)" value={expired.value}
          detail={expired.grace_period ? `${expired.expired} expired · ${expired.grace_period} in grace` : null}
          onClick={go("/hpc/members?status=expired")} />
      ) : <PanelState state={sectionState(expired, failed)} reason={expired?.reason} height={58} />}
      {sectionState(pending, failed) === "ready" ? (
        <>
          <AlertRow Icon={HourglassEmptyIcon} color={PALETTE.amber} label="Pending approval" value={pending.pending}
            detail={waiting(pending.oldest_pending_days)} onClick={go("/hpc/members?status=pending")} />
          <AlertRow Icon={HourglassEmptyIcon} color={PALETTE.blue} label="Under review" value={pending.under_review}
            detail={waiting(pending.oldest_under_review_days)} onClick={go("/hpc/members?status=under_review")} />
        </>
      ) : <PanelState state={sectionState(pending, failed)} reason={pending?.reason} height={58} />}
      {zkOn && (sectionState(door, failed) === "ready" ? (
        <>
          <AlertRow Icon={LockOutlinedIcon} color={PALETTE.red} label="Door blocked" value={door.blocked_on_device}
            detail={`Door access: ${mode.label}`} onClick={go("/hpc/attendance-device?tab=door&filter=blocked")} />
          <AlertRow Icon={SyncProblemOutlinedIcon} color={PALETTE.amber} label="Pending sync" value={door.pending_sync}
            onClick={go("/hpc/attendance-device?tab=door&filter=would_block")} />
        </>
      ) : <PanelState state={sectionState(door, failed)} reason={door?.reason} height={58} />)}
    </Panel>
  );
}

// ---------------------------------------------------------------- membership & access summary
function SummaryRow({ Icon, color, label, value }) {
  const s = useSurface();
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, py: 1, borderBottom: `1px solid ${s.border}` }}>
      <Icon sx={{ color, fontSize: 20 }} aria-hidden />
      <Typography sx={{ flex: 1, fontSize: 14, color: s.text }}>{label}</Typography>
      <Typography sx={{ fontSize: 15, fontWeight: 800, color: s.text }}>{value ?? "—"}</Typography>
    </Box>
  );
}

export function AccessSummary({ summary, failed, zkOn }) {
  const s = useSurface();
  const membership = summary?.membership;
  const device = summary?.device;
  return (
    <Panel icon={<IconBubble Icon={ShieldOutlinedIcon} color={PALETTE.green} size={36} />}
      title="Membership & access" subtitle="Today">
      <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", color: s.muted, textTransform: "uppercase" }}>Membership</Typography>
      <PanelState state={sectionState(membership, failed)} reason={membership?.reason} height={150}>
        <SummaryRow Icon={CheckCircleOutlineIcon} color={PALETTE.green} label="Active access" value={membership?.active_access} />
        <SummaryRow Icon={AcUnitIcon} color={PALETTE.teal} label="Frozen" value={membership?.frozen} />
        <SummaryRow Icon={HourglassEmptyIcon} color={PALETTE.amber} label="Pending approval / under review" value={membership?.pending_approval} />
        <SummaryRow Icon={BlockIcon} color={PALETTE.red} label="Expired or restricted" value={membership?.expired_or_restricted} />
      </PanelState>
      {zkOn && (
        <>
          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", color: s.muted, textTransform: "uppercase", mt: 2 }}>Door device</Typography>
          <PanelState state={sectionState(device, failed)} reason={device?.reason} height={150}>
            <SummaryRow Icon={CheckCircleOutlineIcon} color={PALETTE.green} label="Allowed" value={device?.allow} />
            <SummaryRow Icon={BlockIcon} color={PALETTE.red} label="To block" value={device?.block} />
            <SummaryRow Icon={LockOutlinedIcon} color={PALETTE.red} label="Blocked on device" value={device?.blocked_on_device} />
            <SummaryRow Icon={HelpOutlineIcon} color={PALETTE.grey} label="Not managed" value={device?.skip} />
          </PanelState>
        </>
      )}
    </Panel>
  );
}

// ---------------------------------------------------------------- device status
export function DeviceStatus({ device, failed, go }) {
  const s = useSurface();
  const state = sectionState(device, failed);
  const mode = MODE_META[device?.mode] || MODE_META.off;
  const enabled = (device?.devices || []).filter((d) => d.enabled);
  const errors = device?.recent_errors;
  return (
    <Panel icon={<IconBubble Icon={RouterOutlinedIcon} color={PALETTE.blue} size={36} />}
      title="Device status" subtitle="Door device">
      <PanelState state={state} reason={device?.reason} empty={!enabled.length} height={180}
        emptyText="No enabled device has connected yet.">
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, p: 1.25, borderRadius: 2, bgcolor: `${mode.color}1a` }}>
          <mode.Icon sx={{ color: mode.color }} aria-hidden />
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: s.text }}>Door access: {mode.label}</Typography>
        </Box>
        {device?.heartbeat_stale && (
          <Alert severity="warning" variant="outlined" sx={{ mb: 1.5, fontSize: 13 }}>
            No heartbeat for {device.heartbeat_stale_minutes || 10}+ minutes
          </Alert>
        )}
        {enabled.map((d) => (
          <Box key={d.serial_number} sx={{ mb: 1.5 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: s.text }}>{d.name || d.serial_number}</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              {d.heartbeat_stale
                ? <WarningAmberOutlinedIcon sx={{ fontSize: 18, color: PALETTE.amber }} aria-hidden />
                : <CheckCircleOutlineIcon sx={{ fontSize: 18, color: PALETTE.green }} aria-hidden />}
              <Typography sx={{ fontSize: 13, color: s.text }}>
                {d.heartbeat_stale ? "No recent heartbeat" : "Online"} · last {d.last_seen_at ? `${timeOf(d.last_seen_at)} on ${shortDate(d.last_seen_at.slice(0, 10))}` : "never"}
                {d.last_seen_minutes_ago != null ? ` (${plural(d.last_seen_minutes_ago, "minute")} ago)` : ""}
              </Typography>
            </Box>
            {device.sync_tracked && (
              <Typography sx={{ fontSize: 13, color: s.muted }}>
                Last sync: {d.last_sync_at ? d.last_sync_at.slice(0, 16) : "not yet"}{d.paused ? " · paused" : ""}
              </Typography>
            )}
          </Box>
        ))}
        {device?.last_applied_at && (
          <Typography sx={{ fontSize: 13, color: s.muted, mb: 1 }}>
            Last confirmed change: {device.last_applied_at.slice(0, 16)}
          </Typography>
        )}
        <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", color: s.muted, textTransform: "uppercase", mt: 1 }}>
          Recent sync problems
        </Typography>
        <PanelState state={sectionState(errors, failed)} reason={errors?.reason} empty={!errors?.items?.length}
          emptyText="None recorded." height={40}>
          {(errors?.items || []).map((e) => (
            <Box key={e.id} sx={{ display: "flex", gap: 1, alignItems: "flex-start", py: 0.5 }}>
              <SyncProblemOutlinedIcon sx={{ fontSize: 18, color: PALETTE.amber, mt: 0.25 }} aria-hidden />
              <Typography sx={{ fontSize: 13, color: s.text, minWidth: 0, wordBreak: "break-word" }}>
                {ACCESS_EVENT_LABELS[e.event] || titleCase(e.event)}{e.pin ? ` · PIN ${e.pin}` : ""}{e.detail ? ` · ${e.detail}` : ""}
                <Box component="span" sx={{ color: s.muted }}> · {e.created_at?.slice(0, 16)}</Box>
              </Typography>
            </Box>
          ))}
        </PanelState>
        <Button size="small" sx={{ mt: 1, textTransform: "none", fontSize: 13 }} onClick={go("/hpc/attendance-device?tab=door")}>
          Gym door page
        </Button>
      </PanelState>
    </Panel>
  );
}

// ---------------------------------------------------------------- promotions (finance only)
const exactLkr = (value) => {
  const n = Number(value) || 0;
  return formatLkr(n, Number.isInteger(n) ? 0 : 2);
};

const PROMOTION_STATUS = {
  ACTIVE: { label: "Running", color: PALETTE.green },
  EXPIRED: { label: "Ended", color: PALETTE.grey },
  SCHEDULED: { label: "Scheduled", color: PALETTE.blue },
  DISABLED: { label: "Disabled", color: PALETTE.red },
  DRAFT: { label: "Draft", color: PALETTE.grey },
};

const promotionColumns = [
  { field: "code", headerName: "Code", flex: 1, minWidth: 120, cellClassName: "name-column--cell" },
  { field: "promotion_type", headerName: "Type", flex: 1, minWidth: 130, valueGetter: (v) => typeLabel(v) },
  { field: "discount_value", headerName: "Offer", flex: 0.9, minWidth: 120, valueGetter: (v, row) => valueLabel(row) },
  {
    field: "status", headerName: "Status", flex: 0.8, minWidth: 110,
    renderCell: ({ value }) => {
      const meta = PROMOTION_STATUS[value] || { label: titleCase(value), color: PALETTE.grey };
      return (
        <Chip size="small" label={meta.label} variant="outlined"
          sx={{ fontWeight: 700, fontSize: 12, color: meta.color, borderColor: meta.color }} />
      );
    },
  },
  {
    field: "valid_until", headerName: "Valid until", flex: 0.9, minWidth: 120,
    valueGetter: (v) => (v ? longDate(v) : "No end date"),
  },
  { field: "redeemed", headerName: "Redeemed", flex: 0.7, minWidth: 100, align: "right", headerAlign: "right" },
  { field: "pending", headerName: "Pending", flex: 0.7, minWidth: 90, align: "right", headerAlign: "right" },
  {
    field: "discount", headerName: "Discount", flex: 0.9, minWidth: 120, align: "right", headerAlign: "right",
    valueGetter: (v) => exactLkr(v),
  },
];

function PromotionFigure({ label, value, info }) {
  const s = useSurface();
  return (
    <Box sx={{ flex: "1 1 140px", minWidth: 0, p: 1.25, border: `1px solid ${s.border}`, borderRadius: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Typography sx={{ fontSize: 13, color: s.muted }}>{label}</Typography>
        {info && <InfoTip text={info} />}
      </Box>
      <Typography sx={{ fontSize: 18, fontWeight: 800, color: s.text, wordBreak: "break-word" }}>{value}</Typography>
    </Box>
  );
}

export function PromotionsPanel({ section, failed, range, onPromotion, onReport }) {
  const s = useSurface();
  const state = sectionState(section, failed);
  const rows = section?.promotions || [];
  const running = section?.running_now || 0;
  const used = (section?.codes_applied || 0) > 0;
  const hiddenOthers = (section?.other_codes_used || 0) - rows.filter((r) => r.status !== "ACTIVE").length;
  return (
    <Panel
      icon={<IconBubble Icon={LocalOfferOutlinedIcon} color={PALETTE.amber} size={36} />}
      title="Promotions"
      subtitle={`Running today · use between ${rangeText(range.from, range.to)}`}
      info="Figures for the dates match the Promotions report. Running = switched on and within its dates today."
      action={(
        <Button size="small" variant="outlined" onClick={onReport} sx={{ textTransform: "none", fontSize: 13 }}>
          Promotions report
        </Button>
      )}
    >
      <PanelState state={state} reason={section?.reason} empty={!used && !running} height={220}
        emptyText={`No promotions running, and none used between ${rangeText(range.from, range.to)}`}>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
          <PromotionFigure label="Running now" value={running.toLocaleString()}
            info="Switched on and within its valid dates today, used or not." />
          <PromotionFigure label="Codes applied" value={(section?.codes_applied ?? 0).toLocaleString()}
            info="Every registration that used a code in these dates, including cancelled or reversed ones." />
          <PromotionFigure label="Redeemed" value={(section?.redeemed ?? 0).toLocaleString()} />
          <PromotionFigure label="Pending" value={(section?.pending ?? 0).toLocaleString()}
            info="Reserved at registration, not paid yet." />
          <PromotionFigure label="Discount given" value={exactLkr(section?.total_discount)} />
          <PromotionFigure label="Paid on promoted registrations" value={exactLkr(section?.total_paid)} />
        </Box>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: s.text, mb: 0.25 }}>
          Running promotions and codes used
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: s.muted, mb: 1 }}>
          Every running promotion is listed, even if no code was used yet. Redeemed, pending and discount are for the
          selected dates.{hiddenOthers > 0 ? ` ${plural(hiddenOthers, "more ended code")} not shown.` : ""}
        </Typography>
        {rows.length ? (
          <SmallGrid rows={rows} columns={promotionColumns} getRowId={(r) => r.promotion_id}
            onRowClick={({ row }) => row.promotion_id && onPromotion(row.promotion_id)} />
        ) : (
          <Typography sx={{ fontSize: 13, color: s.muted }}>No promotion is running today.</Typography>
        )}
      </PanelState>
    </Panel>
  );
}
